if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        /* B7: quiet forms never open the drawer — no dialog to announce. */
        if (document.querySelector('cart-drawer') && !this.hasAttribute('data-lunai-quiet'))
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        formData.append(
          'sections',
          this.cartSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        if (this.cart) this.cart.setActiveElement(document.activeElement);
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              /* Chat 6: Shopify answers a PARTIAL add on this same error
                 branch — ask for 3 of a 1-in-stock variant and it replies
                 "Zaradi razpoložljivosti je bil v košarico dodan samo 1 kos."
                 …having added that 1. Dawn then returns without touching the
                 cart chrome, so the line was in the cart while the header
                 bubble and the bottom-nav badge still read 0 and the shopper
                 had every reason to believe the add had failed. Re-sync from
                 the server: on a genuine failure this is a no-op, and on a
                 partial one it tells the truth. */
              this.refreshCartChrome();

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal && quickAddModal.classList.contains('lunai-qa')) {
              /* R-QA add behaviour: NO drawer, NO navigation. The three
                 success signals replace them — button "Dodano" + check
                 (~1.2s), header count tick, then the sheet closes itself
                 and ModalDialog returns focus to the opening bag icon. */
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.updateCartSectionsQuietly(response);
              });
              this.showLunaiAddedState(quickAddModal);
            } else if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      /* P-09: under the cart-page model there is no cart
                         surface to render — keep the chrome truthful. */
                      if (this.cart) this.cart.renderContents(response);
                      else this.updateCartSectionsQuietly(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (this.hasAttribute('data-lunai-quiet') || !this.cart) {
              /* B7 (founder 2026-07-20): the main PDP CTA adds silently
                 too — the drawer stays closed; button "Dodano" + the
                 header count tick are the success signals.
                 P-09: with the drawer retired there is no cart surface to
                 render at all, so EVERY form takes this path. Dawn's
                 stock behaviour here was `window.location = /cart` — a
                 forced navigation on every add, which is exactly the
                 interruption the approved model rules out. */
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.updateCartSectionsQuietly(response);
              });
              this.showLunaiAddedStateInline();
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      /* P-09: which cart sections this form has to keep truthful. With a
         drawer or a notification present that surface decides; with
         neither (the approved cart-page model) the header bubble IS the
         cart chrome — and it still has to tick, or C6-4 comes back. */
      cartSectionsToRender() {
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          return this.cart.getSectionsToRender();
        }
        /* No `selector`: the bubble is an <a id="cart-icon-bubble"> in the
           header, not a section mount, so the target is resolved by id and
           the payload's .shopify-section wrapper supplies the innerHTML —
           exactly what CartDrawer.getSectionsToRender() did. Giving this a
           selector would retarget the FIRST .shopify-section on the page. */
        return [{ id: 'cart-icon-bubble' }];
      }

      /* Chat 6: the same quiet refresh, but for the paths where the add
         response carries no `sections` payload — Shopify's partial-add
         error. Fetches the cart chrome, so there is exactly one update
         mechanism, not two. */
      refreshCartChrome() {
        const ids = this.cartSectionsToRender()
          .map((section) => section.id)
          .join(',');
        if (!ids) return;
        fetch(`${window.location.pathname}?sections=${ids}`)
          .then((response) => response.json())
          .then((sections) => this.updateCartSectionsQuietly({ sections }))
          .catch(() => {});
      }

      /* R-QA: refresh the drawer + cart bubble from the add response
         WITHOUT opening the drawer (renderContents would). Keeps the
         drawer truthful for its next manual open. */
      updateCartSectionsQuietly(response) {
        if (!response.sections) return;
        this.cartSectionsToRender().forEach((section) => {
          const html = response.sections[section.id];
          const target = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!html || !target) return;
          const source = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector(section.selector || '.shopify-section');
          if (source) target.innerHTML = source.innerHTML;
        });
        if (this.cart) {
          this.cart.classList.remove('is-empty');
          /* the innerHTML swap dropped the overlay's close binding */
          const overlay = this.cart.querySelector('#CartDrawer-Overlay');
          if (overlay && typeof this.cart.close === 'function') {
            overlay.addEventListener('click', this.cart.close.bind(this.cart));
          }
        }
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          bubble.classList.add('lunai-tick');
          setTimeout(() => bubble.classList.remove('lunai-tick'), 500);
        }
      }

      /* P4 (pre-launch QA 2026-08-29): the "Dodano" flip happens on a
         disabled control, which screen readers never announce — speak the
         add through the shared #lunai-live singleton (same element and
         clear-then-set technique as lunai-quick-add). */
      announceLunaiAdded() {
        let live = document.getElementById('lunai-live');
        if (!live) {
          live = document.createElement('span');
          live.id = 'lunai-live';
          live.className = 'visually-hidden';
          live.setAttribute('role', 'status');
          live.setAttribute('aria-live', 'polite');
          document.body.appendChild(live);
        }
        live.textContent = '';
        setTimeout(() => {
          live.textContent =
            (window.lunaiStrings && window.lunaiStrings['cart.added_announcement']) || 'Dodano v košarico.';
        }, 30);
      }

      /* B7: inline success state for the main PDP form — "Dodano" for a
         beat, then the button restores itself (no modal to close).
         Race-proofed: Dawn's .finally strips aria-disabled AFTER this
         runs, so the beat uses the real disabled attribute + re-asserts
         aria-disabled on the next tick; re-entry keeps the ORIGINAL
         label (never captures "Dodano" as the restore value). */
      showLunaiAddedStateInline() {
        this.announceLunaiAdded();
        const btn = this.submitButton;
        const label = this.submitButtonText;
        if (!label) return;
        const added = (window.lunaiStrings && window.lunaiStrings['qa.added']) || 'Dodano';
        if (btn.__lunaiTimer) clearTimeout(btn.__lunaiTimer);
        else if (label.textContent !== added) btn.__lunaiOriginal = label.textContent;
        btn.classList.add('is-added');
        btn.setAttribute('disabled', 'disabled');
        setTimeout(() => btn.setAttribute('aria-disabled', 'true'), 0);
        label.textContent = added;
        btn.__lunaiTimer = setTimeout(() => {
          btn.__lunaiTimer = null;
          btn.classList.remove('is-added');
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
          if (label.textContent === added && btn.__lunaiOriginal) {
            label.textContent = btn.__lunaiOriginal;
          }
        }, 1600);
      }

      /* R-QA: button success state, then auto-close with focus restore. */
      showLunaiAddedState(modal) {
        this.announceLunaiAdded();
        const btn = this.submitButton;
        btn.classList.add('is-added');
        btn.setAttribute('aria-disabled', 'true');
        const label = this.submitButtonText;
        if (label) {
          label.textContent =
            (window.lunaiStrings && window.lunaiStrings['qa.added']) || 'Dodano';
        }
        setTimeout(() => {
          modal.hide(); /* wipes modal content; ModalDialog restores focus */
        }, 1200);
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        /* Chat 6 note: rejecting this deferred on the error branch logs an
           unhandled rejection, and a no-op `.catch()` here does NOT stop it —
           proven live: a caught-but-undispatched deferred stays silent, the
           same deferred once DISPATCHED still surfaces, because a Shopify
           StandardEvents subscriber chains `promise.then(...)` and never
           catches its own derived promise. The only theme-side cure would be
           to stop rejecting, which would lie to that subscriber about genuine
           failures. Left alone deliberately (P2-42). */
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
